import { useEffect, useState } from 'react';
import { EquipmentCategory } from './equipmentTypes';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { CustomModal } from '../CustomModal';
import { EquipmentCategoryFormCard } from './EquipmentCategoryFormCard';
import { saveNewEquipmentCategory } from './equipmentCategorySave';
import { getEquipmentCategoryNameConflictMessage } from './equipmentUnitNameUtils';

interface EquipmentCategoryFormOverlayProps {
    visible: boolean;
    onDismiss: () => void;
    onCategoryCreated: (category: EquipmentCategory) => void;
    defaultCategoryName?: string;
}

export const EquipmentCategoryFormOverlay = ({
    visible,
    onDismiss,
    onCategoryCreated,
    defaultCategoryName = '',
}: EquipmentCategoryFormOverlayProps) => {
    const { addCategory, refreshEquipment, companyGroupedEquipmentData } = useEquipmentContext();
    const { company, companyOwner } = useUserContext();

    const [categoryName, setCategoryName] = useState('');
    const [categoryImage, setCategoryImage] = useState<File | null>(null);
    const [categoryImagePreviewFromFile, setCategoryImagePreviewFromFile] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    useEffect(() => {
        if (!visible) return;
        setCategoryName(defaultCategoryName.trim());
        setCategoryImage(null);
        setCategoryImagePreviewFromFile('');
        setSaveError(null);
        setIsSaving(false);
    }, [visible, defaultCategoryName]);

    useEffect(() => {
        if (!categoryImage) {
            setCategoryImagePreviewFromFile('');
            return;
        }
        const url = URL.createObjectURL(categoryImage);
        setCategoryImagePreviewFromFile(url);
        return () => URL.revokeObjectURL(url);
    }, [categoryImage]);

    const handleSave = async () => {
        if (!categoryName.trim()) {
            setSaveError('Equipment category name is required.');
            return;
        }
        if (!companyId) {
            setSaveError('Company context is not available.');
            return;
        }

        const conflictMessage = getEquipmentCategoryNameConflictMessage(
            categoryName,
            companyGroupedEquipmentData,
        );
        if (conflictMessage) {
            setSaveError(conflictMessage);
            return;
        }

        setSaveError(null);
        setIsSaving(true);

        try {
            const newCategory = await saveNewEquipmentCategory({
                companyId,
                name: categoryName,
                imageFile: categoryImage,
            });

            addCategory(newCategory);
            await refreshEquipment();
            onCategoryCreated(newCategory);
            onDismiss();
        } catch (error) {
            console.error('Error creating equipment category:', error);
            const message =
                (error as { errors?: Array<{ message?: string }> })?.errors?.[0]?.message ||
                (error instanceof Error ? error.message : null);
            setSaveError(message || 'Failed to save equipment category. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <CustomModal
            visible={visible}
            onDismiss={onDismiss}
            header="New Equipment Category"
            size="large"
            preferBodyFocus
        >
            {saveError && <p className="unsaved-changes-error">{saveError}</p>}
            <EquipmentCategoryFormCard
                wrapInCard={false}
                showHeader={false}
                autoFocusFirstField
                isCategoryEditMode={false}
                categoryName={categoryName}
                onCategoryNameChange={setCategoryName}
                categoryImagePreviewUrl={categoryImagePreviewFromFile}
                onCategoryImageSelect={(file) => {
                    setCategoryImage(file);
                }}
                onCancel={onDismiss}
                onSave={handleSave}
                isSaving={isSaving}
            />
        </CustomModal>
    );
};
